import { apiCallEx, type ApiResult } from "../../../../apiClient";
import type {
  DocumentIntelligenceHistoryItem,
  DocumentIntelligencePlaybook,
  DocumentIntelligenceProject,
  DocumentIntelligenceSourceMode,
  DocumentIntelligenceThread,
  DocumentIntelligenceWorkflow,
} from "./types";

function failureMessage(result: ApiResult, fallback: string): string {
  return result.data?.error
    || result.data?.message
    || result.data?.rejected?.[0]?.error
    || fallback;
}

async function requireResult<T>(
  request: Promise<ApiResult>,
  pick: (data: any) => T,
  fallback: string
): Promise<T> {
  const result = await request;
  if (!result.ok) throw new Error(failureMessage(result, fallback));
  return pick(result.data);
}

export const documentIntelligenceApi = {
  status: () => requireResult(
    apiCallEx("/docint/status"),
    (data) => data,
    "Document Intelligence is unavailable."
  ),

  projects: () => requireResult<DocumentIntelligenceProject[]>(
    apiCallEx("/docint/workspaces"),
    (data) => data?.workspaces || [],
    "Projects could not be loaded."
  ),

  activityWorkspace: () => requireResult<DocumentIntelligenceProject>(
    apiCallEx("/docint/activity-workspace", {
      method: "POST",
      body: JSON.stringify({}),
    }, "return"),
    (data) => data?.workspace,
    "A private Document Intelligence session could not be started."
  ),

  project: (projectId: string) => requireResult<DocumentIntelligenceProject>(
    apiCallEx(`/docint/workspaces/${encodeURIComponent(projectId)}`),
    (data) => data?.workspace,
    "The project could not be loaded."
  ),

  createProject: (input: { name: string; description?: string; client_id?: number | null }) =>
    requireResult<DocumentIntelligenceProject>(
      apiCallEx("/docint/workspaces", {
        method: "POST",
        body: JSON.stringify(input),
      }, "return"),
      (data) => data?.workspace,
      "The project could not be created."
    ),

  deleteProject: async (projectId: string): Promise<void> => {
    const result = await apiCallEx(`/docint/workspaces/${encodeURIComponent(projectId)}`, {
      method: "DELETE",
      body: JSON.stringify({ confirm: true }),
    }, "return");
    if (!result.ok) throw new Error(failureMessage(result, "The project could not be deleted."));
  },

  uploadFiles: async (projectId: string, files: File[]): Promise<any> => {
    const body = new FormData();
    files.forEach((file) => body.append("files", file));
    return requireResult(
      apiCallEx(`/docint/workspaces/${encodeURIComponent(projectId)}/upload`, {
        method: "POST",
        body,
      }, "return"),
      (data) => data,
      "The files could not be uploaded."
    );
  },

  processingJob: (jobId: string) => requireResult<any>(
    apiCallEx(`/docint/jobs/${encodeURIComponent(jobId)}`),
    (data) => data?.job,
    "Document processing status could not be loaded."
  ),

  documentPreview: (projectId: string, versionId: string) => requireResult<any>(
    apiCallEx(`/docint/workspaces/${encodeURIComponent(projectId)}/versions/${encodeURIComponent(versionId)}/preview`),
    (data) => data?.preview,
    "The document preview could not be loaded."
  ),

  documentSources: (input: {
    workspace_id?: string;
    request_id?: string;
    document_id?: string;
    limit?: number;
  } = {}) => requireResult<any[]>(
    apiCallEx("/docint/documents/query", {
      method: "POST",
      body: JSON.stringify({ limit: 500, ...input }),
    }, "return"),
    (data) => data?.sources || [],
    "Available drafts and repository documents could not be loaded."
  ),

  addVersion: async (projectId: string, versionId: string): Promise<"added" | "duplicate"> => {
    const result = await apiCallEx(`/docint/workspaces/${encodeURIComponent(projectId)}/versions`, {
      method: "POST",
      body: JSON.stringify({ version_id: versionId }),
    }, "return");
    if (result.status === 409) return "duplicate";
    if (!result.ok) throw new Error(failureMessage(result, "The selected document could not be added."));
    return "added";
  },

  removeVersion: async (projectId: string, versionId: string): Promise<void> => {
    const result = await apiCallEx(
      `/docint/workspaces/${encodeURIComponent(projectId)}/versions/${encodeURIComponent(versionId)}`,
      { method: "DELETE" },
      "return"
    );
    if (!result.ok) throw new Error(failureMessage(result, "The document could not be removed."));
  },

  history: () => requireResult<DocumentIntelligenceHistoryItem[]>(
    apiCallEx("/docint/history"),
    (data) => data?.items || data?.history || [],
    "History could not be loaded."
  ),

  sourceModes: () => requireResult<DocumentIntelligenceSourceMode[]>(
    apiCallEx("/docint/source-modes"),
    (data) => data?.source_modes || [],
    "Knowledge sources could not be loaded."
  ),

  playbooks: () => requireResult<DocumentIntelligencePlaybook[]>(
    apiCallEx("/docint/playbooks"),
    (data) => data?.playbooks || [],
    "Playbooks could not be loaded."
  ),

  workflows: () => requireResult<DocumentIntelligenceWorkflow[]>(
    apiCallEx("/docint/workflows"),
    (data) => data?.workflows || [],
    "Workflows could not be loaded."
  ),

  prompts: () => requireResult<any[]>(
    apiCallEx("/prompts"),
    (data) => data?.prompts || [],
    "Saved prompts could not be loaded."
  ),

  improvePrompt: (prompt: string, context?: string) => requireResult<string>(
    apiCallEx("/prompts/optimize", {
      method: "POST",
      body: JSON.stringify({ prompt, context }),
    }, "return"),
    (data) => data?.optimized,
    "The prompt could not be improved."
  ),

  savePrompt: (input: { name: string; prompt_text: string; category?: string }) =>
    requireResult<any>(
      apiCallEx("/prompts", {
        method: "POST",
        body: JSON.stringify(input),
      }, "return"),
      (data) => data?.prompt,
      "The prompt could not be saved."
    ),

  threads: (projectId: string) => requireResult<DocumentIntelligenceThread[]>(
    apiCallEx(`/docint/workspaces/${encodeURIComponent(projectId)}/assistant/threads`),
    (data) => data?.threads || [],
    "Threads could not be loaded."
  ),

  thread: (threadId: string) => requireResult<DocumentIntelligenceThread>(
    apiCallEx(`/docint/assistant/threads/${encodeURIComponent(threadId)}`),
    (data) => ({ ...(data?.thread || {}), messages: data?.messages || data?.thread?.messages || [] }),
    "The thread could not be loaded."
  ),

  createThread: (projectId: string, title?: string) =>
    requireResult<DocumentIntelligenceThread>(
      apiCallEx(`/docint/workspaces/${encodeURIComponent(projectId)}/assistant/threads`, {
        method: "POST",
        body: JSON.stringify(title ? { title } : {}),
      }, "return"),
      (data) => data?.thread,
      "The thread could not be created."
    ),

  sendMessage: (
    threadId: string,
    input: {
      content: string;
      document_version_ids: string[];
      source_modes: string[];
      source_configuration?: object;
      plan_only?: boolean;
    }
  ) => requireResult<any>(
    apiCallEx(`/docint/assistant/threads/${encodeURIComponent(threadId)}/messages`, {
      method: "POST",
      body: JSON.stringify(input),
    }, "return"),
    (data) => data,
    "The message could not be sent."
  ),

  // `approveThread` was removed with the plan-approval screen. Plan mode is gone: a
  // question starts the work. Threads created before that change and still parked in
  // `awaiting_approval` are resumed server-side, not released by a client call, so
  // leaving a client method for the retired endpoint would only invite one back.

  deleteThread: (threadId: string) => requireResult<boolean>(
    apiCallEx(`/docint/assistant/threads/${encodeURIComponent(threadId)}`, {
      method: "DELETE",
    }, "return"),
    () => true,
    "The thread could not be deleted."
  ),

  askProject: (
    projectId: string,
    input: {
      question: string;
      document_version_ids: string[];
      source_modes: string[];
    }
  ) => requireResult<any>(
    apiCallEx(`/docint/workspaces/${encodeURIComponent(projectId)}/ask`, {
      method: "POST",
      body: JSON.stringify(input),
    }, "return"),
    (data) => data,
    "The question could not be answered."
  ),

  createTable: (
    projectId: string,
    input: {
      name: string;
      document_version_ids: string[];
      columns: Array<{ key: string; label: string; prompt: string; format: string }>;
      table_settings: Record<string, string>;
    }
  ) => requireResult<any>(
    apiCallEx(`/docint/workspaces/${encodeURIComponent(projectId)}/tabular-reviews`, {
      method: "POST",
      body: JSON.stringify({ ...input, workflow_key: "ad_hoc_review" }),
    }, "return"),
    (data) => data?.review,
    "The review table could not be created."
  ),

  table: (reviewId: string) => requireResult<any>(
    apiCallEx(`/docint/tabular-reviews/${encodeURIComponent(reviewId)}`),
    (data) => data?.review,
    "The review table could not be loaded."
  ),

  tableExecution: async (reviewId: string): Promise<any | null> => {
    const result = await apiCallEx(`/docint/tabular-reviews/${encodeURIComponent(reviewId)}/execution`);
    // A newly created table does not have an execution until the user runs it.
    if (result.status === 404) return null;
    if (!result.ok) throw new Error(failureMessage(result, "The review progress could not be loaded."));
    return result.data?.execution || null;
  },

  suggestColumns: (prompt: string) => requireResult<any[]>(
    apiCallEx("/docint/tabular/column-builder", {
      method: "POST",
      body: JSON.stringify({ prompt }),
    }, "return"),
    (data) => data?.columns || [],
    "Columns could not be generated."
  ),

  updateTable: (reviewId: string, expectedRevision: number, patch: Record<string, unknown>) =>
    requireResult<any>(
      apiCallEx(`/docint/tabular-reviews/${encodeURIComponent(reviewId)}`, {
        method: "PATCH",
        body: JSON.stringify({ ...patch, expected_revision: expectedRevision }),
      }, "return"),
      (data) => data?.review,
      "The review table could not be updated."
    ),

  startTableExecution: (reviewId: string) => requireResult<any>(
    apiCallEx(`/docint/tabular-reviews/${encodeURIComponent(reviewId)}/execution`, {
      method: "POST",
      body: JSON.stringify({ concurrency: 4 }),
    }, "return"),
    (data) => data?.execution,
    "The review could not be started."
  ),

  processTableExecution: (reviewId: string, expectedRevision: number) => requireResult<any>(
    apiCallEx(`/docint/tabular-reviews/${encodeURIComponent(reviewId)}/execution/process`, {
      method: "POST",
      body: JSON.stringify({ expected_revision: expectedRevision }),
    }, "return"),
    (data) => data?.execution,
    "The review could not be processed."
  ),

  stopTableExecution: (reviewId: string, expectedRevision?: number) => requireResult<any>(
    apiCallEx(`/docint/tabular-reviews/${encodeURIComponent(reviewId)}/execution/cancel`, {
      method: "POST",
      body: JSON.stringify(expectedRevision ? { expected_revision: expectedRevision } : {}),
    }, "return"),
    (data) => data?.execution,
    "The review could not be stopped."
  ),

  redline: (redlineId: string) => requireResult<any>(
    apiCallEx(`/docint/redlines/${encodeURIComponent(redlineId)}`),
    (data) => data,
    "The draft could not be loaded."
  ),

  askRedline: (redlineId: string, content: string, expectedRevision?: number) => requireResult<any>(
    apiCallEx(`/docint/redlines/${encodeURIComponent(redlineId)}/messages`, {
      method: "POST",
      body: JSON.stringify({
        content,
        ...(expectedRevision ? { expected_revision: expectedRevision } : {}),
      }),
    }, "return"),
    (data) => data,
    "The draft question could not be answered."
  ),

  createRedline: (input: {
    workspace_id: string;
    version_id: string;
    instructions: string;
    precedent_version_ids?: string[];
  }) => requireResult<any>(
    apiCallEx("/docint/redlines", {
      method: "POST",
      body: JSON.stringify(input),
    }, "return"),
    (data) => data,
    "The draft review could not be started."
  ),

  decideRedlineChange: (
    redlineId: string,
    changeId: string,
    decision: "accept" | "reject",
    expectedRevision?: number
  ) =>
    requireResult<any>(
      apiCallEx(
        `/docint/redlines/${encodeURIComponent(redlineId)}/changes/${encodeURIComponent(changeId)}/${decision}`,
        {
          method: "POST",
          body: JSON.stringify(expectedRevision ? { expected_revision: expectedRevision } : {}),
        },
        "return"
      ),
      (data) => data,
      `The change could not be ${decision}ed.`
    ),

  generateRedline: (redlineId: string, expectedRevision: number) => requireResult<any>(
    apiCallEx(`/docint/redlines/${encodeURIComponent(redlineId)}/generate`, {
      method: "POST",
      body: JSON.stringify({ expected_revision: expectedRevision }),
    }, "return"),
    (data) => data,
    "The reviewed Word document could not be generated."
  ),
};
